// src/components/shared/BeerCard.jsx
// ============================================================
// BEER CARD - Reusable beer display component
// ============================================================
// Used in: DashboardBeersTab, DashboardOnDeckTab, TapList
// Displays beer info with optional tap number and rating button.
// ============================================================

import React from 'react';
import { Star } from 'lucide-react';

/**
 * BeerCard - Displays a beer with image, details, and actions
 * @param {Object} keg - The keg object containing currentBeer
 * @param {Object} account - Account object for fallback logo
 * @param {Function} calculateAverageRating - Rating calculation function
 * @param {Function} getTotalRatings - Total ratings count function
 * @param {Function} renderStars - Star rendering function
 * @param {Function} onOpenRatingModal - Handler to open rating modal
 * @param {Function} onViewKeg - Handler to navigate to keg detail
 * @param {string} borderColor - Tailwind border class (e.g., 'border-amber-500')
 * @param {boolean} showTapNumber - Whether to display tap number column
 * @param {boolean} showStatus - Whether to display status badge
 * @param {Object} location - Location object with tapNames array
 * @param {string} sublocationName - Sublocation name to find sublocation tapNames
 */
export const BeerCard = ({ 
  keg, 
  account,
  calculateAverageRating,
  getTotalRatings,
  renderStars,
  onOpenRatingModal, 
  onViewKeg,
  borderColor = 'border-amber-500',
  showTapNumber = false,
  showStatus = false,
  location = null,
  sublocationName = null
}) => {
  // Helper to get tap display name from location tapNames array
  const getTapDisplayName = () => {
    if (!keg.tapNumber) return '-';
    
    // Find tapNames array from location or sublocation
    let tapNames = [];
    if (location) {
      if (sublocationName && location.sublocations) {
        const sublocation = location.sublocations.find(s => s.name === sublocationName);
        tapNames = sublocation?.tapNames || [];
      } else {
        tapNames = location.tapNames || [];
      }
    }
    
    // If tapNames configured, use it; otherwise fall back to raw tap number
    if (tapNames.length > 0) {
      // tapNumber is 1-based, array is 0-based
      const tapIndex = parseInt(keg.tapNumber) - 1;
      return tapNames[tapIndex] || keg.tapNumber;
    }
    
    return keg.tapNumber;
  };
  const beer = keg.currentBeer;
  if (!beer) return null;

  // Calculate rating data if functions provided
  const avgRating = calculateAverageRating && keg.beerHistory?.length > 0
    ? (() => {
        const currentBeerHistory = keg.beerHistory.find(b => b.name === beer.name);
        return currentBeerHistory?.sessions 
          ? calculateAverageRating(currentBeerHistory.sessions) 
          : 0;
      })()
    : 0;

  const totalRatings = getTotalRatings && keg.beerHistory?.length > 0
    ? (() => {
        const currentBeerHistory = keg.beerHistory.find(b => b.name === beer.name);
        return currentBeerHistory?.sessions 
          ? getTotalRatings(currentBeerHistory.sessions) 
          : 0;
      })()
    : 0;

  return (
    <div className={`border-l-4 ${borderColor} pl-4 py-3 grid grid-cols-[auto_1fr_auto] gap-4 items-center overflow-hidden`}>
      {/* Left: Image and Details */}
      <div className="flex gap-4 items-start min-w-0">
        {/* Beer/Brewery Image */}
        {(beer.imageUrl || account?.profile?.logoUrl) && (
          <img 
            src={beer.imageUrl || account.profile.logoUrl} 
            alt={beer.name} 
            className="w-20 h-20 object-cover rounded flex-shrink-0" 
          />
        )}
        
        {/* Beer Info */}
        <div className="min-w-0">
          <h5 className="text-lg font-semibold whitespace-nowrap overflow-hidden text-ellipsis text-gray-900 dark:text-gray-100">
            {beer.name}
            <span className="text-sm italic text-gray-600 dark:text-gray-400 ml-2">
              {beer.brewery || account?.profile?.name}
            </span>
          </h5>
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap overflow-hidden text-ellipsis">
            {beer.style} • {beer.abv}% ABV 
            {beer.ibu && ` • ${beer.ibu} IBU`}
          </p>
          {beer.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 break-words">
              {beer.description}
            </p>
          )}

          {/* Days on Tap */}
          {keg.current_beer_started_at && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">On tap: {(() => {
              const started = new Date(keg.current_beer_started_at);
              const now = new Date();
              const diffMs = now - started;
              const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              if (days >= 1) return `${days} day${days === 1 ? '' : 's'}`;
              const hours = Math.floor(diffMs / (1000 * 60 * 60));
              if (hours >= 1) return `${hours}h`;
              const minutes = Math.floor(diffMs / (1000 * 60));
              return `${minutes}m`;
            })()}</p>
          )}
          
          {/* Rating Display */}
          {renderStars && totalRatings > 0 && (
            <div className="mt-2 flex items-center gap-2">
              {renderStars(avgRating)}
              <span className="text-xs text-gray-500">
                ({totalRatings} rating{totalRatings !== 1 ? 's' : ''})
              </span>
            </div>
          )}
          
          {/* Status Badge (for on-deck view) */}
          {showStatus && (
            <div className="mt-2">
              <span className="px-2 py-1 text-xs rounded bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 font-medium">
                {keg.tapStatus}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Center: Tap Number (optional) */}
      {showTapNumber ? (
        <div className="flex flex-col items-center justify-center">
          <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Tap
          </div>
          <div className="text-4xl font-bold text-amber-600">
            {getTapDisplayName()}
          </div>
        </div>
      ) : (
        <div /> /* Empty spacer to maintain grid */
      )}

      {/* Right: Actions */}
      <div className="flex flex-col gap-2 items-end">
        <button 
          onClick={() => onOpenRatingModal(keg)} 
          className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 transition text-sm flex items-center gap-1 whitespace-nowrap"
        >
          <Star className="w-4 h-4" />
          Rate
        </button>
        <button 
          onClick={() => onViewKeg(keg.id)} 
          className="text-amber-600 hover:text-amber-700 font-medium text-sm transition whitespace-nowrap"
        >
          View →
        </button>
      </div>
    </div>
  );
};

export default BeerCard;
